/* tslint:disable:indent */
import { FormGroup, ValidatorFn } from '@angular/forms';
import { LogService } from '../../../services/log.service';
import { FormInput } from './form.input';
import { Field } from '../../../../formulaires/eforms/model/field';
import { TypeAnn } from '../../../../formulaires/common/models/type.ann';
import { ValidatorService } from '../../../services/validator.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { EformsSubjectService } from '../../../../formulaires/eforms/services/eforms-subject.service';
import { EformControl } from '../../../models/eformControl';
import { EformGroup } from '../../../models/eformGroup';
import { EformArray } from '../../../models/eformArray';
import { CanNavigateInStepperService } from '../../../services/can-navigate-in-stepper.service';
import { AvisDataTransferService } from '../../../services/avis-data-transfer.service';
import { EformConditionsService } from '../../../../formulaires/eforms/services/eform-conditions.service';
import { EformDataService } from '../../../../formulaires/eforms/services/eform-data.service';
import { EformDuplicableFieldsService } from '../../../../formulaires/eforms/services/eform-duplicable-fields.service';
import cloneDeep from 'lodash.clonedeep';
import { DuplicableElementsIdSchemeEnum } from '../../../../formulaires/eforms/enum/duplicable-elements-id-scheme.enum';
import { ConditionsCreationMapService } from '../../../services/conditions-creation-map.service';

export class FormInputFactory {

  static validatorService = new ValidatorService();
  static eformDataService = new EformDataService();
  static eformDuplicableFieldsService = new EformDuplicableFieldsService();
  static eformConditionService = new EformConditionsService(FormInputFactory.eformDataService, FormInputFactory.eformDuplicableFieldsService);
  static eformsSubjectService = new EformsSubjectService(FormInputFactory.eformDataService, FormInputFactory.eformConditionService);
  static conditionsCreationMapService = new ConditionsCreationMapService();
  /**
   * si c'est true donc ignorer le traitement des éléments répétables
   * @private
   */
  private static IGNORE_REPEATABLE = false;
  private static LOG: LogService = new LogService(FormInputFactory);
  private static fieldsToExpriment = ['BT-531-Procedure'];
  private LOG: LogService = new LogService(FormInputFactory);

  /**
   * Contexte : lors du chargement d'un lot (élément dupliable), le composant/FormGroup peut être
   * détruit et recréé plusieurs fois de suite (navigation entre onglets, rechargement asynchrone
   * de l'entité dupliable). À chaque recréation, cette factory est réappelée depuis zéro et
   * getValue() peut retourner une valeur vide AVANT que les vraies données n'arrivent - or comme
   * le FormGroup est intégralement recréé (pas juste le contrôle), rien ne permet de savoir qu'une
   * bonne valeur avait déjà été calculée lors d'un appel précédent : une réévaluation tardive avec
   * une valeur vide peut alors écraser silencieusement une valeur correcte déjà affichée.
   *
   * Ce cache mémorise, par clé unique de champ (repeatablePathWithIndex + field.id), la dernière
   * valeur NON VIDE connue pour un champ 'time'. Avant d'accepter une valeur vide, on vérifie si le
   * cache contient déjà une valeur : si oui, on la réutilise plutôt que d'afficher un champ vide.
   *
   * Volontairement restreint aux champs 'time' pour l'instant (cf. discussion) : c'est le seul cas
   * concret identifié à ce jour, et généraliser à tous les types demanderait de vérifier au préalable
   * qu'aucun effet de bord n'existe pour des champs où une valeur vide EST parfois une valeur
   * légitime à afficher (contrairement au cas 'time' où le vide n'est ici qu'un artefact de timing).
   */
  private static timeFieldValueCache: Map<string, string> = new Map();

  /**
   * Contexte : à la création d'un F16 depuis PLACE, les données complètes de tous les lots
   * (DLRO comprise) sont disponibles très tôt (cf. avisWithElementsDuplicable côté
   * e-forms-generator), alors que le circuit qui alimente réellement les champs affichés
   * (fetch par lot via l'entité dupliable) est asynchrone et peut, à la toute première création,
   * renvoyer une valeur vide avant que les vraies données n'arrivent. En pré-remplissant ce cache
   * dès que les données complètes sont connues, la toute première évaluation du champ 'time'
   * peut déjà s'appuyer dessus, sans attendre un second passage.
   */
  public static prefillTimeFieldCache(key: string, value: string): void {
    if (key && value !== null && value !== undefined && value !== '') {
      FormInputFactory.timeFieldValueCache.set(key, value);
    }
  }

  /**
   * Accès public en lecture au cache 'time', utilisé comme filet de
   * sécurité final directement dans form-input.component.ts (ngOnInit), qui s'exécute de façon
   * fiable à chaque changement de lot - contrairement à certains chemins internes de la factory
   * qui peuvent, pour un contrôle déjà existant partagé entre lots, ne pas recalculer sa valeur.
   */
  public static getTimeFieldCacheValue(key: string): string | undefined {
    return FormInputFactory.timeFieldValueCache.get(key);
  }

  /**
   * Extrait l'index de lot utilisé comme clé du cache 'time' à partir
   * du prefixId (ex: 'GR-Lot_3_2' -> '3'), en gérant le cas particulier de l'index -1.
   *
   * Au tout premier chargement du formulaire, aucun lot n'a encore été explicitement sélectionné :
   * EformDuplicableEntityService.actuaEntityIndex vaut alors -1 et prefixId ressemble à
   * 'GR-Lot_-1_2'. Or c'est bien le lot 1 (index 0) qui est affiché à ce moment-là. Sans ce
   * traitement, la clé de cache ne correspondait à rien pour le lot 1 : son champ heure restait
   * vide, et - plus grave - la sauvegarde suivante réécrivait cette valeur vide en base, écrasant
   * la DLRO pourtant correctement reçue au préremplissage.
   */
  public static extractLotIndexForCache(prefixId: string): string | null {
    const lotIndexMatch = prefixId?.match(/_(-?\d+)_/);
    if (!lotIndexMatch) {
      return null;
    }
    return lotIndexMatch[1] === '-1' ? '0' : lotIndexMatch[1];
  }

  /**
   * cette méthode permet de créer un champ angular
   * @param field
   * @param parentForm
   * @param disabledValue
   */
  public static createEformsElement(field: Field, parentForm, disabledValue, repeatablePath, noticeValue,
                                    repeatablePathWithIndex, formName, prefixId, modalValue, groupOrSectionId, translateService, arrayLength, lastElementArray, eformsSubjectService, fieldLastArray, canNavigateInStepperService, avisDataTransferService, repeatablePathForDuplicableElement): FormInput {
    const formInput = new FormInput();
    let sharedPath = field.isJsonPathAlreadyUsed;
    let parentName;

    if (prefixId != undefined) {
      if (!prefixId.includes('_')) {
        prefixId += '_0_0';
      } else if (prefixId.split('_').length === 2 && field.isNewFromSameStep) {
        prefixId = prefixId.split('_')[0] + '_0_0';
      } else if (prefixId.split('_').length === 2) {
        prefixId = prefixId.split('_')[0] + '_0_' + prefixId.split('_')[1];
      }
    }

    if (fieldLastArray == null && field.jsonPath != null && field.jsonPath != undefined && field._repeatable === true) {
      fieldLastArray = field.jsonPath.substring(0, field.jsonPath.lastIndexOf('[]'));
      fieldLastArray = fieldLastArray.substring(fieldLastArray.lastIndexOf('.') + 1);
    }
    repeatablePathWithIndex = repeatablePathWithIndex?.replaceAll('.val_', '_');
    this.eformsSubjectService = eformsSubjectService;

    if (this.fieldsToExpriment.includes(field.id)) {
      this.LOG.debug('');
    }
    let localformGroup = null;
    let name = null;
    const pathElements: Array<string> = field.jsonPath.split('.');
    const numberPathElements = pathElements.length;
    formInput.elements = pathElements;
    // si le jsonPath est alimenté
    // le nom de l'élément est le dernier élément dans le jsonPAth
    name = pathElements[numberPathElements - 1];

    if (numberPathElements === 1) {
      throw new Error('EFORMS_FIELD_ERROR: impossible de trouver un controle name au premier niveau');
    } else {
      // le json path contient plusieurs éléments donc il faut avancer jusqu'au niveau
      let it = 0;
      let currentFormGroup = parentForm;
      // tslint:disable-next-line: forin
      // tslint:disable-next-line:forin
      for (const index in pathElements) {
        let currentElement = pathElements[index];
        let conditionsCreation;
        if (field.conditionsCreationMap && field.conditionsCreationMap[currentElement.replaceAll('[]', '')]) {
          conditionsCreation = field.conditionsCreationMap[currentElement.replaceAll('[]', '')];
        }
        if (+index > 0) {
          if (currentFormGroup.parent?.name) {
            currentFormGroup.parent.name = parentName?.replace('[]', '');
          }
          parentName = pathElements[+index];
        }
        it++;
        // ignorer le traitement des éléments répétables
        if (this.IGNORE_REPEATABLE) {
          currentElement = currentElement.replaceAll('[]', '');
        }


        const isRepeatable = currentElement.includes('[]');

        // pour chaque élément soit créer un formControl soit utilisé un existant
        if (it === pathElements.length) {
          // dernier élement ajout d'un control Name
          name = currentElement;
          if (repeatablePathWithIndex) {
            formInput.sepSep = '';
            [...repeatablePathWithIndex.matchAll(/_(\d+)_/g)].forEach(id => {
              formInput.sepSep += '_' + id[1];

            });
          }
          if (currentElement.endsWith('[]')) {
            // ici on traite le cas ou le dernier élément est répétable sauf que il y a deux cas qui peuvent se présenter
            // cas 1 : on un tableau mais coté afichage pas de duplication
            // cas 2 : on a un tableau avec un bouton ajouter

            // pour distinguer les deux cas nous pouvons se baser sur la valuer de l'attribut repeatable
            if (!field._repeatable) {
              name = name.replace('[]', '');
              currentFormGroup.addControl(name, new EformArray([], parentName.replace('[]', ''), groupOrSectionId));
              if(repeatablePathForDuplicableElement.getValue()){
                repeatablePathWithIndex = this.transformIndexedPath(repeatablePathForDuplicableElement.getValue())
              }
              currentFormGroup.controls[name].push(
                new EformControl(prefixId, field.id + formInput.sepSep, field.type, {
                  value: this.getValue(noticeValue, field.jsonPath, repeatablePathWithIndex, modalValue, repeatablePath, field, sharedPath, currentFormGroup.idInput, 'val'),
                  disabled: disabledValue
                }, this.getValidators(field, formName))
              );
              formInput.type = 'arrayEforms';
            } else {
              name = name.replace('[]', '');
              // ✅ FIX: Vérifier si le contrôle existe déjà avant de le créer
              if (!currentFormGroup.contains(name)) {
                currentFormGroup.addControl(name, new EformArray([], parentName.replace('[]', ''), groupOrSectionId));
                currentFormGroup.controls[name].push(new EformGroup(prefixId, groupOrSectionId, parentName, conditionsCreation, {}));
              }
              // Utiliser l'élément existant à l'index 0
              currentFormGroup = currentFormGroup.controls[name][0];
            }
          } else {
            if(repeatablePathForDuplicableElement.getValue()){
              repeatablePathWithIndex = this.transformIndexedPath(repeatablePathForDuplicableElement.getValue())
            }
            let returnedValue = this.getValue(noticeValue, field.jsonPath, repeatablePathWithIndex, modalValue, repeatablePath, field, sharedPath, currentFormGroup.idInput, 'val');

            if (returnedValue === null && field.mandatory?.includes(formName?.split('E_FORM_')[1]) && this.doesNotHaveMandatoryWithCondition(field, formName)) {
              returnedValue = '';
            }
            if (returnedValue === '' && !field.mandatory?.includes(formName?.split('E_FORM_')[1])) {
              returnedValue = null;
            }
            if (field.type === 'date' && returnedValue?.length > 10) {
              returnedValue = returnedValue.substring(0, 10);
            }
            if (field.type === 'time' && returnedValue?.length > 10) {
              returnedValue = returnedValue.substring(11, 19);
            }
            // cache de secours pour les champs 'time' (cf. commentaire sur
            // FormInputFactory.timeFieldValueCache). Clé unique par occurrence du champ.
            //
            // repeatablePathWithIndex est TOUJOURS '..._INDEX_0' quel que
            // soit le lot réellement affiché (un seul lot est chargé à la fois pour des raisons de
            // perf - cf. commentaire dans ngOnInit), donc il ne peut PAS servir à différencier les
            // lots entre eux dans la clé de cache. On utilise à la place l'index de lot réellement
            // affiché, extrait de prefixId (ex: 'GR-Lot_3_2' -> 3), qui reflète actuaEntityIndex
            // (cf. EformDuplicableEntityService.getDuplicableEntity) et varie donc bien par lot.
            // Etendu au type 'date' (cf. BT-131(d)-Lot), même
            // problématique que 'time' au premier rendu d'un lot.
            if (field.type === 'time' || field.type === 'date') {
              const lotIndexForCache = FormInputFactory.extractLotIndexForCache(prefixId);
              const timeCacheKey = lotIndexForCache !== null
                ? `${lotIndexForCache}_SEPSEP_${field.id}`
                : `${repeatablePathWithIndex}_SEPSEP_${field.id}`;
              const isReturnedValueEmpty = returnedValue === null || returnedValue === undefined || returnedValue === '';
              if (isReturnedValueEmpty) {
                const cachedTimeValue = FormInputFactory.timeFieldValueCache.get(timeCacheKey);
                if (cachedTimeValue) {
                  returnedValue = cachedTimeValue;
                }
              } else {
                FormInputFactory.timeFieldValueCache.set(timeCacheKey, returnedValue);
              }
            }
            returnedValue = this.specificField(canNavigateInStepperService, returnedValue, field);
            canNavigateInStepperService.currentValue = returnedValue;
            if (!currentFormGroup.contains(name)) {
              currentFormGroup.addControl(
                name,
                new EformControl(prefixId, field.id + formInput.sepSep, field.type, {
                  value: returnedValue,
                  disabled: disabledValue
                }, this.getValidators(field, formName))
              );
            } else {
              // 🔧 FIX 2026-03-16: Pour les champs avec isJsonPathAlreadyUsed (ex: BT-15-Lot / BT-615-Lot),
              // ne PAS écraser le contrôle existant si getValue retourne null ou vide.
              // Cela arrive quand deux champs partagent le même jsonPath mais pointent vers des éléments
              // différents du tableau (ex: non-restricted-document vs restricted-document).
              // Si le 2e champ (ex: BT-615-Lot) ne trouve pas son élément, il retournerait null
              // et écraserait la valeur du 1er champ (BT-15-Lot) qui partage le même FormControl.
              // ⚠️ IMPORTANT: Inclure '' (chaîne vide) car les champs mandatory sans mandatoryWithCondition
              // voient leur null converti en '' à la ligne 157-158, ce qui échappait à l'ancienne vérification.
              //
              // Généralisation à TOUS les champs, pas seulement isJsonPathAlreadyUsed.
              // Constaté sur BT-131(t)-Lot (heure de la DLRO) : la factory est réévaluée plusieurs fois
              // pour un même lot (rechargement asynchrone de l'entité dupliable), et une réévaluation
              // tardive peut retourner "" alors qu'une valeur correcte ("14:30:00") a déjà été appliquée
              // juste avant. Sans ce garde-fou généralisé, ce "" écrase silencieusement la bonne valeur
              // via setControl(), ce qui affiche un champ vide à l'écran malgré une donnée backend correcte.
              //
              // ALM47 : cette protection est limitée aux contrôles 'val', c'est-à-dire à la valeur saisie
              // ou chargée d'un champ. Elle ne s'applique pas aux contrôles d'attribut (listName,
              // unitcode), sauf pour les champs isJsonPathAlreadyUsed comme à l'origine.
              // Pourquoi : un contrôle d'attribut peut avoir été pré-rempli par un AUTRE champ du même
              // groupe. Exemple : dans le groupe noticetypecode, setListName() appelé pour le champ
              // « Type d'avis » (BT-02-notice) écrit listName = 'notice-type'. Le champ « Type de
              // formulaire » (BT-03-notice) utilise ce même contrôle listName et sa valeur lue est
              // toujours vide. Si on conservait 'notice-type', sa valeur par défaut ('competition',
              // 'result'…) ne s'appliquerait jamais (setDefaultValue ne remplit qu'un contrôle vide) et
              // le XML partirait avec listName="notice-type", rejeté par la validation eForms
              // (règle BR-BT-00003-0052 : erreur affichée sur tous les avis eForms).
              const existingValue = currentFormGroup.get(name)?.value;
              const isReturnedValueEmpty = returnedValue === null || returnedValue === undefined || returnedValue === '';
              const isExistingValueMeaningful = existingValue !== null && existingValue !== undefined && existingValue !== '';
              const shouldPreserveExistingValue = isReturnedValueEmpty && isExistingValueMeaningful
                && (name === 'val' || !!field.isJsonPathAlreadyUsed);

              if (shouldPreserveExistingValue) {
                // Ne pas écraser la valeur existante par une valeur vide issue d'une réévaluation tardive
              } else if (field.type === 'time' || field.type === 'date') {
                // NE PAS remplacer l'instance du FormControl pour un
                // champ 'time'. setControl() substitue une nouvelle instance dans le FormGroup, mais
                // la directive formControlName de l'<input> reste liée à l'ANCIENNE instance (Angular
                // ne re-bind pas une directive déjà initialisée quand on remplace le contrôle sous
                // elle). L'input affichait donc la valeur de l'ancien contrôle (vide) alors que le
                // groupe contenait bien la bonne valeur - d'où le symptôme "1re visite du lot : champ
                // vide / 2e visite : champ correctement valorisé" (à la 2e visite, le bon contrôle
                // est déjà en place au moment où la directive s'initialise).
                // En mettant à jour la valeur sur l'instance existante, la propagation vers le DOM se
                // fait normalement via le ControlValueAccessor, dès la première visite.
                const existingControl = currentFormGroup.get(name);
                if (existingControl && existingControl.value !== returnedValue) {
                  existingControl.setValue(returnedValue);
                }
              } else {
                currentFormGroup.setControl(
                  name,
                  new EformControl(prefixId, field.id + formInput.sepSep, field.type, {
                    value: returnedValue,
                    disabled: disabledValue
                  }, this.getValidators(field, formName))
                );
              }
            }
            this.setDefaultValue(field, currentFormGroup, translateService, formName, parentName.replace('[]', ''), avisDataTransferService);
            this.setDefaultValueGroup(field, parentForm, repeatablePathWithIndex, parentName.replace('[]', ''), prefixId, conditionsCreation);
            this.setMultilingual(field, currentFormGroup, parentName.replace('[]', ''));
            this.setCurrency(field, currentFormGroup, parentName.replace('[]', ''), noticeValue, repeatablePathWithIndex, modalValue, repeatablePath, sharedPath);
            this.setUnitCode(field, currentFormGroup, parentName.replace('[]', ''), noticeValue, repeatablePathWithIndex, modalValue, repeatablePath, sharedPath);
            this.setListName(field, currentFormGroup, parentName.replace('[]', ''));
            this.setInputId(field, currentFormGroup, parentName.replace('[]', ''));
            this.initWithIdScheme(field, currentFormGroup, parentName.replace('[]', ''));
            this.setPresetValue(field, currentFormGroup, parentName.replace('[]', ''));
          }
          localformGroup = currentFormGroup;
        } else {
          // dans le cas ou le repeatablePath est rempli
          // si c'est au milieu du chemin c soit un formGroup soit un formArray
          if (isRepeatable) {
            currentElement = currentElement.replaceAll('[]', '');
            if (fieldLastArray == currentElement) {
              if (this.eformsSubjectService.getField(pathElements, parentForm, repeatablePathWithIndex)) {
                // ✅ FIX: Vérifier si un élément existe déjà avant d'en créer un nouveau
                if (currentFormGroup.controls[currentElement].controls.length === 0) {
                  currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
                }
                currentFormGroup = currentFormGroup.controls[currentElement].controls[currentFormGroup.controls[currentElement].controls.length - 1];
              } else {
                if ((currentFormGroup as FormGroup).contains(currentElement) || currentFormGroup.controls[currentElement]) {
                  let num = 0;
                  let tmpPathRepeatable;
                  if (repeatablePath) {
                    num = +(repeatablePath.split('_SEPSEP_')[1]);
                    tmpPathRepeatable = repeatablePath.split('_SEPSEP_')[0];
                  }
                  if (repeatablePath && repeatablePath !== '' && tmpPathRepeatable.endsWith(currentElement + '[]')) {
                    let i = currentFormGroup.controls[currentElement].length;
                    if (repeatablePathWithIndex) {
                      formInput.index = this.eformDataService.getArrayIndex(currentElement + '[]', repeatablePathWithIndex);
                    }
                    // ✅ FIX: Ne créer un nouveau groupe que si nécessaire
                    const targetIndex = formInput.index || 0;
                    if (i <= targetIndex) {
                      currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
                      i = currentFormGroup.controls[currentElement].length;
                    }
                    currentFormGroup = currentFormGroup.controls[currentElement].controls[i - 1];
                  } else {
                    let indexValue = 0;

                    if (repeatablePathWithIndex) {
                      formInput.index = this.eformDataService.getArrayIndex(currentElement + '[]', repeatablePathWithIndex);
                      indexValue = formInput.index;
                    }
                    if (currentFormGroup.controls[currentElement].controls.length === 0 ||
                      currentFormGroup.controls[currentElement].controls.length <= indexValue ||
                      (arrayLength > currentFormGroup.controls[currentElement].controls.length && currentElement === lastElementArray)) {
                      currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
                      const i = currentFormGroup.controls[currentElement].length;
                      currentFormGroup = currentFormGroup.controls[currentElement].controls[i === 0 ? i : i - 1];
                      // dans ce cas il faut créer un controle
                    } else {
                      currentFormGroup = currentFormGroup.controls[currentElement].controls[currentElement === lastElementArray ? arrayLength - 1 : indexValue];
                    }

                  }
                } else {
                  currentFormGroup.addControl(currentElement, new EformArray([], parentName.replace('[]', ''), groupOrSectionId));
                  currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name.replace('[]', ''), conditionsCreation, {}));
                  currentFormGroup = currentFormGroup.controls[currentElement].controls[0];
                }
              }
            } else if ((currentFormGroup as FormGroup).contains(currentElement) || currentFormGroup.controls[currentElement]) {
              let num = 0;
              let tmpPathRepeatable;
              if (repeatablePath) {
                num = +(repeatablePath.split('_SEPSEP_')[1]);
                tmpPathRepeatable = repeatablePath.split('_SEPSEP_')[0];
              }
              if (repeatablePath && repeatablePath !== '' && tmpPathRepeatable.endsWith(currentElement + '[]')) {
                let i = currentFormGroup.controls[currentElement].length;
                if (repeatablePathWithIndex) {
                  formInput.index = this.eformDataService.getArrayIndex(currentElement + '[]', repeatablePathWithIndex);
                }
                if (i >= (num + 1) && i != 0) {
                  currentFormGroup = currentFormGroup.controls[currentElement].controls[i - 1];
                } else {
                  currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
                  i = currentFormGroup.controls[currentElement].length;
                  currentFormGroup = currentFormGroup.controls[currentElement].controls[i - 1];
                }
              } else {
                let indexValue = 0;

                if (repeatablePathWithIndex) {
                  formInput.index = this.eformDataService.getArrayIndex(currentElement + '[]', repeatablePathWithIndex);
                  if(!repeatablePathWithIndex.includes('procurementprojectlot') && currentElement ==='specifictendererrequirement'){
                    formInput.index = 0;
                  }
                  indexValue = formInput.index;
                }

                if (currentFormGroup.controls[currentElement].controls.length === 0 ||
                  currentFormGroup.controls[currentElement].controls.length <= indexValue ||
                  (arrayLength > currentFormGroup.controls[currentElement].controls.length && currentElement === lastElementArray) ||
                  // Ne créer un nouveau groupe que si indexValue > 0 ET qu'on a besoin d'un nouveau groupe
                  // Cela évite les doublons quand plusieurs champs partagent le même chemin de tableau (ex: partylegalentity[])
                  (indexValue > 0 && this.isNewEformGroupToCreate(prefixId, currentFormGroup, currentElement))) {

                  const i = this.pushEformGroupAtRightPlace(currentFormGroup, currentElement, prefixId, groupOrSectionId, noticeValue, pathElements, name, conditionsCreation);
                  currentFormGroup = currentFormGroup.controls[currentElement].controls[i];
                  // dans ce cas il faut créer un controle
                } else {
                  let indexIfFound;
                  if (conditionsCreation && !+indexValue) {

                    let indexToShiftAvisWithElementsDuplicable = 0;
                    indexIfFound = currentFormGroup.controls[currentElement].controls.findIndex((c => this.eformConditionService.verifyConditionsCreationWithControls(c, c.conditionsCreation, conditionsCreation)));

                    if (indexIfFound != -1) {
                      currentFormGroup = currentFormGroup.controls[currentElement].controls[indexIfFound];
                      if (!currentFormGroup.conditionsCreation) {
                        currentFormGroup.conditionsCreation = [];
                      }
                      indexToShiftAvisWithElementsDuplicable = indexIfFound;
                      currentFormGroup.conditionsCreation = Array.from(new Set(currentFormGroup.conditionsCreation.concat(field.conditionsCreationMap[currentElement])));
                    } else {
                      // 🔧 FIX: Chercher dans TOUS les groupes existants, pas seulement l'index 0
                      // Cela évite les doublons pour les éléments comme awardcriterionparameter
                      // avec différents listName (number-threshold, number-weight, number-fixed)
                      // MAIS seulement si:
                      // 1. isJsonPathAlreadyUsed est false
                      // 2. Le listName est référencé dans le conditionsCreationMap de cet élément
                      const fieldListName = field.listname;
                      let foundGroupIndex = -1;
                      let foundGroup = null;

                      // Vérifier si le listName est pertinent pour cet élément (référencé dans ses conditions)
                      const listNameIsInConditions = fieldListName && field.conditionsCreationMap?.[currentElement]?.some(
                        (cond: string) => cond.toLowerCase().includes(`listname='${fieldListName.toLowerCase()}'`)
                      );
                      // Vérifier si le schemeName est pertinent pour cet élément (référencé dans ses conditions)
                      const fieldSchemeName = field._schemeName;
                      const schemeNameIsInConditions = fieldSchemeName && field.conditionsCreationMap?.[currentElement]?.some(
                        (cond: string) => cond.toLowerCase().includes(`schemename='${fieldSchemeName.toLowerCase()}'`)
                      );

                      // Recherche par schemeName en priorité (plus spécifique que listName)
                      if (fieldSchemeName && schemeNameIsInConditions && currentFormGroup.controls[currentElement]?.controls) {
                        foundGroupIndex = currentFormGroup.controls[currentElement].controls.findIndex(g => {
                          const groupSchemeName = g?.get('schemeName')?.value;
                          if (groupSchemeName && groupSchemeName.toLowerCase() === fieldSchemeName.toLowerCase()) {
                            return true;
                          }
                          if (g?.conditionsCreation) {
                            const conditionsStr = JSON.stringify(g.conditionsCreation).toLowerCase();
                            return conditionsStr.includes(`schemename='${fieldSchemeName.toLowerCase()}'`);
                          }
                          return false;
                        });
                        if (foundGroupIndex !== -1) {
                          foundGroup = currentFormGroup.controls[currentElement].controls[foundGroupIndex];
                        }
                      }

                      if (!foundGroup && fieldListName && !field.isJsonPathAlreadyUsed && listNameIsInConditions && currentFormGroup.controls[currentElement]?.controls) {
                        // Chercher un groupe existant avec le même listName
                        foundGroupIndex = currentFormGroup.controls[currentElement].controls.findIndex(g => {
                          // Vérifier le listName du groupe
                          const groupListName = g?.get('listName')?.value;
                          if (groupListName && groupListName.toLowerCase() === fieldListName.toLowerCase()) {
                            return true;
                          }
                          // Vérifier dans les conditionsCreation
                          if (g?.conditionsCreation) {
                            const conditionsStr = JSON.stringify(g.conditionsCreation).toLowerCase();
                            return conditionsStr.includes(`listname='${fieldListName.toLowerCase()}'`);
                          }
                          return false;
                        });

                        if (foundGroupIndex !== -1) {
                          foundGroup = currentFormGroup.controls[currentElement].controls[foundGroupIndex];
                        }
                      }

                      if (foundGroup) {
                        // Utiliser le groupe trouvé avec le même listName
                        currentFormGroup = foundGroup;
                        if (!currentFormGroup.conditionsCreation) {
                          currentFormGroup.conditionsCreation = [];
                        }
                        currentFormGroup.conditionsCreation = Array.from(new Set(currentFormGroup.conditionsCreation.concat(field.conditionsCreationMap[currentElement])));
                        indexToShiftAvisWithElementsDuplicable = foundGroupIndex;
                      } else {
                        // Fallback: vérifier le groupe à l'index 0 pour compatibilité
                        const existingGroup = currentFormGroup.controls[currentElement].controls.length > 0
                          ? currentFormGroup.controls[currentElement].controls[0]
                          : null;

                        let canReuseExistingGroup = false;
                        if (existingGroup && indexValue === 0) {
                          const existingListName = existingGroup?.get('listName')?.value;

                          if (existingListName && fieldListName) {
                            canReuseExistingGroup = existingListName.toLowerCase() === fieldListName.toLowerCase();
                          } else if (!fieldListName) {
                            canReuseExistingGroup = true;
                          } else if (!existingListName && existingGroup.conditionsCreation) {
                            const existingConditionsStr = JSON.stringify(existingGroup.conditionsCreation);
                            canReuseExistingGroup = existingConditionsStr.includes(`listName='${fieldListName}'`) ||
                              existingConditionsStr.includes(`listname='${fieldListName}'`);
                          } else if (!existingListName && !existingGroup.conditionsCreation) {
                            canReuseExistingGroup = true;
                          }

                          // Un groupe avec des conditionsCreation incompatibles ne peut pas être réutilisé.
                          // Ex: conditionsCreation=["[ID/&schemeName='Lot']"] ≠ ["[ID/&schemeName='LotsGroup']"]
                          if (canReuseExistingGroup && conditionsCreation?.length > 0) {
                            const existingConds = existingGroup?.conditionsCreation;
                            if (existingConds?.length > 0) {
                              const existingCondStr = JSON.stringify(existingConds).toLowerCase();
                              const newCondStr = JSON.stringify(conditionsCreation).toLowerCase();
                              if (existingCondStr !== newCondStr) {
                                canReuseExistingGroup = false;
                              }
                            }
                          }
                        }

                        if (canReuseExistingGroup) {
                          currentFormGroup = existingGroup;
                          if (!currentFormGroup.conditionsCreation) {
                            currentFormGroup.conditionsCreation = [];
                          }
                          currentFormGroup.conditionsCreation = Array.from(new Set(currentFormGroup.conditionsCreation.concat(field.conditionsCreationMap[currentElement])));
                          indexToShiftAvisWithElementsDuplicable = 0;
                        } else {
                          indexToShiftAvisWithElementsDuplicable = currentFormGroup.controls[currentElement].length;
                          const newGroup = new EformGroup(prefixId, groupOrSectionId, name.replace('[]', ''), conditionsCreation, {});
                          // 🔧 FIX: Stocker le listName immédiatement sur le nouveau groupe
                          // pour que les champs suivants avec le même listName le trouvent
                          // MAIS seulement si:
                          // 1. isJsonPathAlreadyUsed est false
                          // 2. Le listName est référencé dans le conditionsCreationMap de cet élément
                          //    (sinon le listName appartient à un champ enfant, pas au groupe intermédiaire)
                          const listNameIsInConditions = fieldListName && field.conditionsCreationMap?.[currentElement]?.some(
                            (cond: string) => cond.toLowerCase().includes(`listname='${fieldListName.toLowerCase()}'`)
                          );
                          if (fieldListName && !field.isJsonPathAlreadyUsed && listNameIsInConditions) {
                            newGroup.setControl('listName', new EformControl(null, null, field.type, fieldListName));
                          }
                          // 🔧 Stocker le schemeName sur le nouveau groupe (comme listName)
                          // pour un lookup fiable dans updateField et createEformsElement.
                          if (fieldSchemeName && schemeNameIsInConditions) {
                            newGroup.setControl('schemeName', new EformControl(null, null, 'text', fieldSchemeName));
                          }
                          currentFormGroup.controls[currentElement].push(newGroup);
                          currentFormGroup = currentFormGroup.controls[currentElement].controls[currentFormGroup.controls[currentElement].controls.length - 1];
                        }
                      }
                    }
                    if(repeatablePathForDuplicableElement.getValue()){
                      const regex = new RegExp(currentElement + '\\[\\]\\d+', 'g');
                      let newPath = String(repeatablePathForDuplicableElement.getValue()).replace(regex, currentElement+'[]'+indexToShiftAvisWithElementsDuplicable);
                      if(!repeatablePathForDuplicableElement.getValue().includes('procurementprojectlot') && newPath.includes('tendererqualificationrequest[]1')){
                        newPath = newPath.replace(new RegExp('specifictendererrequirement' + '\\[\\]\\d+', 'g'), 'specifictendererrequirement[]0')
                      }
                      repeatablePathForDuplicableElement.next(newPath);
                    }
                  } else {
                    // 🔧 FIX: Chercher d'abord par listName dans tous les groupes existants
                    // même quand conditionsCreation n'est pas défini
                    // MAIS seulement si isJsonPathAlreadyUsed est false
                    const fieldListName = field.listname;
                    let foundByListName = false;

                    if (fieldListName && !field.isJsonPathAlreadyUsed && currentFormGroup.controls[currentElement]?.controls) {
                      const foundIdx = currentFormGroup.controls[currentElement].controls.findIndex(g => {
                        const groupListName = g?.get('listName')?.value;
                        if (groupListName && groupListName.toLowerCase() === fieldListName.toLowerCase()) {
                          return true;
                        }
                        if (g?.conditionsCreation) {
                          const conditionsStr = JSON.stringify(g.conditionsCreation).toLowerCase();
                          return conditionsStr.includes(`listname='${fieldListName.toLowerCase()}'`);
                        }
                        return false;
                      });

                      if (foundIdx !== -1) {
                        currentFormGroup = currentFormGroup.controls[currentElement].controls[foundIdx];
                        foundByListName = true;
                      }
                    }

                    if (!foundByListName) {
                      if (currentFormGroup.controls[currentElement].controls.filter(c => c.prefixEform !== undefined && c.prefixEform === prefixId).length != 1) {
                        indexIfFound = -1;
                      } else {
                        indexIfFound = currentFormGroup.controls[currentElement].controls.findIndex((c => c.prefixEform !== undefined && c.prefixEform === prefixId));
                      }
                      if (indexIfFound != -1) {
                        currentFormGroup = currentFormGroup.controls[currentElement].controls[indexIfFound];
                      } else {
                        currentFormGroup = currentFormGroup.controls[currentElement].controls[currentElement === lastElementArray ? arrayLength - 1 : indexValue];
                      }
                    }
                  }

                }

              }
            } else {

              currentFormGroup.addControl(currentElement, new EformArray([], parentName.replace('[]', ''), groupOrSectionId));
              currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name.replace('[]', ''), conditionsCreation, {}));
              currentFormGroup = currentFormGroup.controls[currentElement].controls[0];
            }
          } else {
            //pas un formArray
            if ((currentFormGroup as FormGroup).contains(currentElement)) {
              // si le formGroup existe dèjà donc pointer sur ce formGroup
              currentFormGroup = currentFormGroup.controls[currentElement] as FormGroup;
              // rien à rajouter
            } else {
              // si non rajouter un nouveau formGroup
              currentFormGroup.addControl(currentElement, new EformGroup(prefixId, groupOrSectionId, parentName, conditionsCreation, {}));
              currentFormGroup = currentFormGroup.controls[currentElement] as FormGroup;
            }
          }
        }
      }
    }
    this.LOG.debug('END_EFORMS_FIELD', field.id);


    formInput.name = name.replace('[]', '').replace('[]', '');
    formInput.formGroup = localformGroup;
    formInput.repeatablePathWithIndex = repeatablePathWithIndex;
    return formInput;
  }

  public static initWithIdScheme(field: Field, currentFormGroup: any, name: string): void {
    if (field._idScheme?.toString()) {
      let array: string[] = cloneDeep(this.eformsSubjectService.idSchemeListSubject.getValue());
      const existingValue = currentFormGroup.get('val')?.value;

      // 🔧 FIX : Pour les éléments duplicables, TOUJOURS utiliser la valeur existante si elle existe
      // Cela évite de recalculer un ID incorrect lors du rechargement
      const isDuplicableElement = Object.values(DuplicableElementsIdSchemeEnum).includes(field._idScheme?.toString());

      if (!existingValue || (!isDuplicableElement && array?.includes(existingValue))) {
        // Pas de valeur existante OU (élément non-duplicable ET valeur déjà dans la liste)
        // → Calculer un nouvel ID
        const count = array.filter(e => e.startsWith(field._idScheme.toString())).length + 1;
        const value = field._idScheme.toString() + '-' + count.toString().padStart(4, '0');
        array.push(value);
        currentFormGroup.name = name.replace('[]', '');
        currentFormGroup.setControl('val', new EformControl(null, null, field.type, value));
      } else {
        // Valeur existante → L'utiliser telle quelle
        if (!array.includes(existingValue)) {
          array.push(existingValue);
        }
        // S'assurer que la valeur est bien définie dans le control
        if (!currentFormGroup.get('val')?.value) {
          currentFormGroup.setControl('val', new EformControl(null, null, field.type, existingValue));
        }
      }
      this.eformsSubjectService.setIdSchemeListSubject(array);
    }
  }

  public static setMultilingual(field: Field, currentFormGroup: any, name: string): void {
    if (field.type.toString() === 'text-multilingual') {
      currentFormGroup.name = name.replace('[]', '');
      currentFormGroup.setControl('languageID', new EformControl(null, null, field.type, 'FRA'));
    }
  }

  public static setCurrency(field: Field, currentFormGroup: any, name: string, noticeValue, repeatablePathWithIndex, modalValue, repeatablePath, sharedPath): void {
    if (field.type.toString() === 'currencyID') {
      currentFormGroup.name = name.replace('[]', '');
      currentFormGroup.setControl('currencyid', new EformControl(null, null, field.type, this.getValue(noticeValue, field.jsonPath, repeatablePathWithIndex, modalValue, repeatablePath, field, sharedPath, currentFormGroup.idInput, 'currencyid')));
    }
  }

  public static setUnitCode(field: Field, currentFormGroup: any, name: string, noticeValue, repeatablePathWithIndex, modalValue, repeatablePath, sharedPath): void {
    if (field.type.toString() === 'unitCode') {
      currentFormGroup.name = name.replace('[]', '');
      currentFormGroup.setControl('unitcode', new EformControl(null, null, field.type, this.getValue(noticeValue, field.jsonPath, repeatablePathWithIndex, modalValue, repeatablePath, field, sharedPath, currentFormGroup.idInput, 'unitcode')));
    }
  }

  public static setDefaultValue(field: Field, currentFormGroup: any, translateService: TranslateService, formName: string, name: string, avisDataTransferService: AvisDataTransferService) {
    if (field.defaultValue !== undefined) {
      field.defaultValue.forEach(e => {

        const nameAttribut: string = e.split('=')[0].split('.').pop();
        let valueAttribut: string = e.split('=').pop().replaceAll('\'', '');
        if (field.id === 'BT-757-notice' && currentFormGroup.get('val')?.value !== null && currentFormGroup.get('val')?.value !== '') {
          return;
        }
        if (valueAttribut.includes('eform-not-editable.')) {
          valueAttribut = translateService.instant(valueAttribut);
        }
        if (valueAttribut === 'DATENOW') {
          valueAttribut = moment().format('yyyy-MM-DD');

        }
        if (valueAttribut === 'HOURNOW') {
          valueAttribut = moment().format('HH:mm:ss') + '+01:00';
        }

        //on ne set pas les default value à l'edition a part pour l'uuid
        if (valueAttribut === 'UUID') {
          valueAttribut = currentFormGroup.get('val')?.value !== '' ? currentFormGroup.get('val')?.value : uuidv4();
          currentFormGroup.name = name.replace('[]', '');
          currentFormGroup.setControl(nameAttribut, new EformControl(null, null, field.type, valueAttribut, this.getValidators(field, formName)));
        } else if (currentFormGroup.get(nameAttribut)?.value === '' || currentFormGroup.get(nameAttribut)?.value === null || currentFormGroup.get(nameAttribut)?.value === undefined) {
          currentFormGroup.name = name.replace('[]', '');
          currentFormGroup.setControl(nameAttribut, new EformControl(null, null, field.type, valueAttribut, this.getValidators(field, formName)));
        }
      });
    }
  }

  public static setDefaultValueGroup(field: Field, parentForm, repeatablePathWithIndex, name: string, prefixId, conditionsCreation) {
    if (field.defaultValueGroup) {
      const pathElements: Array<string> = field.defaultValueGroup.split('.');
      const numberPathElements = pathElements.length;

      let currentFormGroup = parentForm;

      for (const index in pathElements) {
        let currentElement = pathElements[index];

        if (Number(index) == numberPathElements - 1) {
          const endDefaultValue = currentElement.split('=');
          let nameAttribut = endDefaultValue[0];
          let valueAttribut = endDefaultValue[1].replaceAll('\'', '');
          currentFormGroup.setControl(nameAttribut, new EformControl(null, null, field.type, valueAttribut));
        } else {
          if (currentElement.includes('[]')) {
            currentElement = currentElement.replace('[]', '');
            if ((currentFormGroup as FormGroup).contains(currentElement)) {
              //temporaire
              let indexIfFound = currentFormGroup.controls[currentElement].controls.findIndex((c => c.prefixEform !== undefined && c.prefixEform === prefixId));
              if (indexIfFound != -1) {
                currentFormGroup = currentFormGroup.controls[currentElement].controls[indexIfFound];
              } else {
                currentFormGroup = currentFormGroup.controls[currentElement].controls[this.eformDataService.getArrayIndex(currentElement + '[]', repeatablePathWithIndex)];
              }
            } else {
              currentFormGroup.addControl(currentElement, new EformArray([], name, null));
              currentFormGroup.controls[currentElement].push(new EformGroup(null, null, name, conditionsCreation, {}));
              currentFormGroup = currentFormGroup.controls[currentElement].controls[0];
            }

          } else {
            if ((currentFormGroup as FormGroup).contains(currentElement)) {
              // si le formGroup existe dèjà donc pointer sur ce formGroup
              currentFormGroup = currentFormGroup.controls[currentElement] as FormGroup;
              // rien à rajouter
            } else {
              // si non rajouter un nouveau formGroup
              currentFormGroup.addControl(currentElement, new EformGroup(null, null, name, conditionsCreation, {}));
              currentFormGroup = currentFormGroup.controls[currentElement] as FormGroup;
            }
          }

        }

      }

    }
  }

  public static setPresetValue(field: Field, currentFormGroup: any, name: string) {
    // 🔧 FIX 2026-02-10: Désactivation des presetValue uniquement pour les champs Unpublish (BT-195, BT-196, BT-197, BT-198)
    if (this.isUnpublishField(field)) {
      return; // Ne pas appliquer de presetValue pour les champs Unpublish
    }

    if (field._presetValue) {
      const currentValue = currentFormGroup.get('val')?.value;
      // Appliquer la presetValue si le champ n'a pas de valeur (null, undefined, ou chaîne vide)
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        currentFormGroup.name = name.replace('[]', '');
        let presetValue = field._presetValue;

        // Gestion de la valeur spéciale {NOW} pour les dates
        if (presetValue === '{NOW}') {
          presetValue = moment().format('YYYY-MM-DD');
        }

        currentFormGroup.setControl('val', new EformControl(null, null, field.type, presetValue));
      }
    }
  }

  /**
   * Vérifie si un champ est un champ "Unpublish" (BT-195, BT-196, BT-197, BT-198)
   * Ces champs ne doivent pas avoir de presetValue appliqué automatiquement
   */
  private static isUnpublishField(field: Field): boolean {
    if (!field?.id) {
      return false;
    }
    // Les champs Unpublish ont des IDs comme BT-195(...), BT-196(...), BT-197(...), BT-198(...)
    return /^BT-19[5678]\(/.test(field.id);
  }

  public static setListName(field: Field, currentFormGroup: any, name: string) {
    currentFormGroup.name = name.replace('[]', '');
    if (!field.jsonPath.includes('listName') && field.listname) {
      const existingListName = currentFormGroup.get('listName')?.value;
      if (!existingListName) {
        currentFormGroup.setControl('listName', new EformControl(null, null, field.type, field.listname));
      } else if (existingListName !== field.listname) {
        // Si un listName différent existe, stocker le mapping inputId -> listName
        let listNameMap = currentFormGroup.get('listNameByInputId')?.value || {};
        listNameMap[field.id] = field.listname;
        currentFormGroup.setControl('listNameByInputId', new EformControl(null, null, field.type, listNameMap));
      }
    }
  }

  public static setInputId(field: Field, currentFormGroup: any, name: string) {
    currentFormGroup.name = name.replace('[]', '');

    // Stocker tous les inputIds dans une liste pour les champs qui partagent le même jsonPath
    const existingInputId = currentFormGroup.get('inputId')?.value;
    if (existingInputId) {
      // Si un inputId existe déjà, ajouter le nouveau à la liste des inputIds alternatifs
      let alternateIds = currentFormGroup.get('alternateInputIds')?.value || [];
      if (!alternateIds.includes(field.id) && existingInputId !== field.id) {
        alternateIds = [...alternateIds, field.id];
        currentFormGroup.setControl('alternateInputIds', new EformControl(null, null, field.type, alternateIds));
      }
    } else {
      currentFormGroup.setControl('inputId', new EformControl(null, null, field.type, field.id));
    }
  }

  public static getValue(object: TypeAnn | String, keys, repeatablePathWithIndex, modalValue, repeatablePath, field, sharedPath, currentFormGroupId, typeVal): any {

    let currentObject = null;
    // Utiliser repeatablePathWithIndex pour le pathUsed car il contient les indices explicites
    let pathUsed = repeatablePathWithIndex ?? repeatablePath;
    let valueArray;
    let keysArray;


    //recup là où il y a le listname
    let lastKey;


    if (!keysArray) {
      keysArray = field.jsonPath.split('.');
    }

    let lastListName = null;
    if (field.listname) {
      lastListName = this.getLastListName(field);
      // 🔧 FIX: Activer sharedPath si le champ a des conditions spéciales dans conditionsCreationMap
      // (pas juste procurementprojectlot). Cela permet à BT-761-Lot de trouver le bon TQR
      // sans affecter les champs répétables comme BT-539-Lot
      if (!sharedPath && this.hasSpecialConditionsCreationMap(field.conditionsCreationMap)) {
        sharedPath = true;
      }
      //Dans certain cas le listname n'est pas renseigné alors qu'il est necessaire (lorsque isJsonPathAlreadyUsed = true),
      //on récupére le listname via l'id
    } else if (field.isJsonPathAlreadyUsed) {
      field.listname = field.id.split('-')[field.id.split('-').length - 1];
      lastListName = this.getLastListName(field);
    } else if (field.conditionsCreationMap) {
      // 🔧 FIX: Extraire le listname depuis conditionsCreationMap si disponible
      // Cela permet aux champs comme BT-772-Lot de trouver le bon TQR
      // On active sharedPath seulement si on trouve un listname dans les conditions (pas juste procurementprojectlot)
      const extractedListname = this.extractListnameFromConditionsCreationMap(field.conditionsCreationMap, field);
      if (extractedListname) {
        field.listname = extractedListname;
        lastListName = this.getLastListName(field);
        sharedPath = true; // Activer la recherche par listname
      }
    }


    if (lastListName && sharedPath) {
      if (modalValue) {
        currentObject = modalValue;
        pathUsed = repeatablePathWithIndex ?? repeatablePath;
      } else {
        currentObject = object;
      }
      valueArray = currentObject;

      let indexVal = keysArray.indexOf('val');
      keysArray = keysArray.slice(0, indexVal);

      lastKey = lastListName.split('.')[keysArray.length];
      let valueAttribut: string = lastListName.split('=').pop().replaceAll('\'', '');

      // 🔧 FIX 2026-03-04: Garder une référence à l'élément parent qui contient l'inputid
      // Pour les champs avec isJsonPathAlreadyUsed=true (ex: BT-541-Lot-WeightNumber),
      // l'inputid est stocké au niveau de awardcriterionparameter[], pas au niveau de parameternumeric
      let parentElementWithInputId = null;

      // 🛡️ Sécurité: compteur pour éviter les boucles infinies
      let iterationCount = 0;
      const MAX_ITERATIONS = 50;

      for (const key in keysArray) {
        iterationCount++;
        if (iterationCount > MAX_ITERATIONS) {
          return null;
        }

        const currentPath = keysArray[key];



        if (valueArray) {
          if (currentPath === keysArray[keysArray.length - 1] && currentPath.includes('[]')) {
            valueArray = valueArray[currentPath.replaceAll('[]', '')];
          } else if (currentPath.includes('[]')) {

            let indexValue = pathUsed ? this.eformDataService.getArrayIndex(currentPath, pathUsed) : 0;
            const tempValue = valueArray[currentPath.replaceAll('[]', '')];

            // 🔧 Pour BT-615-Lot et BT-15-Lot, chercher l'index du document par documenttype
            // Ces deux champs partagent le même jsonPath et sont différenciés par documenttype
            const arrayName = currentPath.replaceAll('[]', '');
            if ((field.id === 'BT-615-Lot' || field.id === 'BT-15-Lot') && arrayName === 'callfortendersdocumentreference') {
              const expectedDocType = field.id === 'BT-615-Lot' ? 'restricted-document' : 'non-restricted-document';
              // IMPORTANT: Les clés sont en MINUSCULES dans le modèle
              const correctIndex = tempValue.findIndex(elem => {
                const docType = elem?.documenttype;
                if (Array.isArray(docType) && docType.length > 0) {
                  return docType[0]?.val === expectedDocType;
                }
                return false;
              });


              if (correctIndex !== -1 && correctIndex !== indexValue) {
                indexValue = correctIndex;
              }
            }

            if (indexValue == 1 && tempValue?.length === 1) {
              indexValue = 0;
            }
            if (tempValue) {
              // 🔧 FIX: Recherche par listname SEULEMENT pour les champs avec conditions spéciales
              // (ex: tendererqualificationrequest, specifictendererrequirement)
              // Pour les éléments répétables normaux (ex: critères d'attribution), on utilise l'index du path
              const hasSpecialConditions = this.hasSpecialConditionsCreationMap(field.conditionsCreationMap);

              if (sharedPath && tempValue.length > 1 && field.listname && hasSpecialConditions) {
                // Chercher l'élément du tableau qui contient un enfant avec le bon listname
                let foundIndex = this.findIndexByListName(tempValue, keysArray, Number(key), field.listname);

                // 🔧 FIX: Si le listname n'est pas trouvé, utiliser conditionsCreationMap
                // Cas spécial: BT-761-Lot cherche un TQR SANS SpecificTendererRequirement
                if (foundIndex === -1 && field.conditionsCreationMap && currentPath.includes('tendererqualificationrequest')) {
                  foundIndex = this.findIndexByConditionsCreationMap(tempValue, field.conditionsCreationMap, currentPath);
                }

                if (foundIndex !== -1) {
                  indexValue = foundIndex;
                }
              } else if (field.conditionsCreationMap && Object.keys(field.conditionsCreationMap).length > 0) {
                // 🔧 FIX F29/F16: N'appliquer ce bloc QUE si currentPath est une clé du conditionsCreationMap.
                // Ex: subordinateawardingcriterion[] n'est PAS dans conditionsCreationMap de BT-539-Lot
                // (qui n'a que procurementprojectlot) → l'indexValue de getArrayIndex (ligne 799) est correct.
                // Ex: awardcriterionparameter[] EST dans conditionsCreationMap de BT-541-Lot-WeightNumber → ok.
                const currentPathKey = currentPath.replace('[]', '').toLowerCase();
                const conditionsCreationMapHasCurrentPath = field.conditionsCreationMap[currentPathKey] != null;

                if (conditionsCreationMapHasCurrentPath) {
                  // D'abord, essayer de trouver par listname enrichi (plus simple et direct)
                  let foundIndex = -1;
                  if (field.listname) {
                    foundIndex = this.findIndexByEnrichedListName(tempValue, keysArray, Number(key), field.listname);
                  }

                  // Si le listname ne trouve rien, essayer conditionsCreationMap
                  if (foundIndex === -1 && field.conditionsCreationMap) {
                    foundIndex = this.findIndexByConditionsCreationMap(tempValue, field.conditionsCreationMap, currentPath);
                  }

                  if (foundIndex !== -1) {
                    indexValue = foundIndex;
                  } else if (!FormInputFactory.eformDuplicableFieldsService.isLoadingAvisLieValue
                    && !FormInputFactory.eformDuplicableFieldsService.isLoadingPreferenceValue) {
                    return null;
                  } else {
                    const pathIndexMatch = pathUsed?.match(/\[]_INDEX_(\d+)/);
                    if (pathIndexMatch) {
                      const pathIndex = parseInt(pathIndexMatch[1], 10);
                      if (pathIndex >= 0 && pathIndex < tempValue.length) {
                        indexValue = pathIndex;
                      } else {
                        indexValue = 0;
                      }
                    } else if (FormInputFactory.eformDuplicableFieldsService.isLoadingAvisLieValue
                      || FormInputFactory.eformDuplicableFieldsService.isLoadingPreferenceValue) {
                      indexValue = 0;
                    } else if (field.isJsonPathAlreadyUsed && field.id) {
                      const foundByInputId = tempValue.findIndex(el => el.inputid === field.id);
                      if (foundByInputId !== -1) {
                        indexValue = foundByInputId;
                      } else {
                        return null;
                      }
                    } else {
                      return null;
                    }
                  }
                }
                // else: currentPath pas dans conditionsCreationMap → indexValue de getArrayIndex déjà correct
              }

              valueArray = tempValue[indexValue];


              // 🔧 FIX 2026-03-04: Stocker la référence à l'élément du tableau si il contient un inputid
              // Cela permet de vérifier plus tard si on est sur le bon élément pour les champs isJsonPathAlreadyUsed
              if (field.isJsonPathAlreadyUsed && valueArray && typeof valueArray === 'object' && valueArray.inputid) {
                parentElementWithInputId = valueArray;
              }
            } else {
              return null;
            }
          } else {
            valueArray = valueArray[currentPath];
          }
          if (!valueArray) {
            if (valueArray === undefined || valueArray === null) {
              return null;
            }
          }
        } else {
          return null;
        }


      }

      // créer manip avec listname pour récup valeur
      let valueReturned;
      if (Array.isArray(valueArray) && valueArray) {

        if (valueArray.length == 1 && (valueArray[0].val != undefined || valueArray[0][lastKey]?.val != undefined)) {
          return valueArray[0].val ?? valueArray[0][lastKey]?.val;
        } else {
          // 🔧 FIX 2026-03-03: Amélioration du filtrage pour prendre en compte l'inputid
          // Pour les champs avec isJsonPathAlreadyUsed=true (ex: BT-541-Lot-WeightNumber),
          // on doit filtrer par inputid en plus du listname
          let valueArrayFiltered = valueArray.filter(it => {
            let matchesListname = true;
            let matchesInputId = true;

            // Vérifier le listname si défini
            if (valueAttribut && lastKey) {
              matchesListname = it[lastKey?.replace('[]', '')]?.listname == valueAttribut;
            }

            // Vérifier l'inputid si le champ a isJsonPathAlreadyUsed=true
            if (field.isJsonPathAlreadyUsed && it.inputid) {
              matchesInputId = it.inputid === field.id;
            }

            return matchesListname && matchesInputId;
          });

          if (currentFormGroupId.split('|')[1]?.split('_')[1] && currentFormGroupId.split('|')[1].split('_')[1] == valueArrayFiltered.length - 1) {
            valueReturned = valueArrayFiltered[currentFormGroupId.split('|')[1].split('_')[1]];
          } else if (valueArrayFiltered?.length > 0) {
            valueReturned = valueArrayFiltered[field.id.split('_').length > 1 ? field.id.split('_')[1] : 0] ?? '';
          } else {
            valueReturned = valueArray[this.eformDataService.getArrayIndex(keysArray[keysArray.length - 1], repeatablePathWithIndex)];
          }
          if (valueReturned) {
            let arrayOfValues = field.jsonPath.replace(keysArray.join('.') + '.', '').split('.');
            arrayOfValues.forEach(it => {
              if (it.includes('[]')) {
                valueReturned = valueReturned[it.replace('[]', '')][0];
              } else if (valueReturned) {
                valueReturned = valueReturned[it];
              }
            });
          }

        }
      }
      // 🔧 FIX: Pour les champs avec isJsonPathAlreadyUsed=true dont le jsonPath contient 'parameternumeric',
      // vérifier que l'inputid de l'objet correspond au field.id demandé.
      // Cela corrige le cas spécifique où BT-541-Lot-FixedNumber récupère la valeur de BT-541-Lot-WeightNumber
      // car ils partagent le même jsonPath (parameternumeric.val) mais ont des inputid différents.
      if (field.isJsonPathAlreadyUsed && field.jsonPath?.includes('parameternumeric') &&
        valueArray && typeof valueArray === 'object' && !Array.isArray(valueArray)) {
        const objectInputId = valueArray.inputid;

        if (objectInputId && objectInputId !== field.id) {
          return null;
        }
      }

      // 🔧 FIX 2026-03-03: Pour les champs avec isJsonPathAlreadyUsed=true et conditionsCreationMap,
      // vérifier que le listname correspond à celui défini dans conditionsCreationMap.
      // Cela corrige le cas où BT-765-Lot (listname='framework-agreement') et BT-766-Lot (listname='dps-usage')
      // partagent le même jsonPath (contractingsystem[].contractingsystemtypecode.val).
      // De même pour tous les champs qui utilisent conditionsCreationMap pour filtrer les éléments d'un tableau.
      if (field.isJsonPathAlreadyUsed && field.conditionsCreationMap && valueArray && typeof valueArray === 'object' && !Array.isArray(valueArray)) {
        const expectedListname = this.extractListnameFromConditionsCreationMap(field.conditionsCreationMap, field);

        if (expectedListname) {
          const objectListname = valueArray.listname;

          if (objectListname && objectListname !== expectedListname) {
            return null;
          }
        }
      }

      // 🔧 FIX 2026-03-04: Vérification finale de l'inputid pour les champs avec isJsonPathAlreadyUsed
      // Il faut vérifier l'inputid sur valueArray (l'objet final) ET sur parentElementWithInputId (l'élément du tableau parent).
      // Cela permet de différencier:
      // 1) BT-541-Lot-WeightNumber vs BT-541-Lot-FixedNumber (inputid sur awardcriterionparameter[])
      // 2) BT-541-Lot-WeightNumber vs BT-5421-Lot (inputid sur parametercode)
      //
      // 🔧 FIX 2026-03-09: Exception pour BT-615-Lot et BT-15-Lot qui partagent le même path
      // (callfortendersdocumentreference[].attachment.externalreference.uri.val)
      // Pour ces champs, la différenciation se fait via le documenttype, pas via l'inputid
      if (field.isJsonPathAlreadyUsed && field.id !== 'BT-615-Lot' && field.id !== 'BT-15-Lot' && valueArray && typeof valueArray === 'object' && !Array.isArray(valueArray)) {
        const objectInputId = valueArray.inputid;

        if (objectInputId && objectInputId !== field.id) {
          return null;
        }
      }

      // Ensuite, si on a un élément parent avec inputid (ex: awardcriterionparameter[]),
      // vérifier également qu'il correspond
      if (field.isJsonPathAlreadyUsed && parentElementWithInputId) {
        const parentInputId = parentElementWithInputId.inputid;

        if (parentInputId && parentInputId !== field.id) {
          return null;
        }
      }

      // Retourner la valeur finale
      const finalValue = valueArray[typeVal] ? valueArray[typeVal] : (valueReturned ?? '');


      return finalValue;
    } else {
      if ((object === undefined || object === null) && (modalValue === undefined || modalValue === null)) {
        return null;
      }
      if (modalValue) {
        currentObject = modalValue;
        pathUsed = repeatablePath;
        keys = keys.slice(keys.indexOf(Object.keys(modalValue)[0]));
      } else {
        currentObject = object;
      }
      keys = keys.toLowerCase();

      if (!keys.includes('.')) {
        // un niveaux
        // TODO : camelCase
        return currentObject[keys];
      } else {
        // plusierus niveau
        // tslint:disable-next-line:prefer-const
        let value;
        const keysArray = keys.split('.');
        value = currentObject;

        // tslint:disable-next-line:forin
        for (const key in keysArray) {
          let currentPath = keysArray[key];
          if (currentPath === 'val') {
            currentPath = typeVal;
          }

          if (currentPath.includes('[]')) {
            let indexValue = pathUsed ? this.eformDataService.getArrayIndex(currentPath, pathUsed) : 0;
            const tempValue = value[currentPath.replaceAll('[]', '')];

            // 🔧 FIX 2026-03-09: Pour les champs avec conditionsCreationMap, trouver l'index correct
            // en utilisant findIndexByConditionsCreationMap au lieu de l'index du path.
            // Exemple: BT-76-Lot doit chercher dans tendererqualificationrequest[] l'élément qui
            // correspond à la condition [not(SpecificTendererRequirement)]
            if (field?.conditionsCreationMap && tempValue && Array.isArray(tempValue)) {
              const pathKey = currentPath.replace('[]', '').toLowerCase();
              if (field.conditionsCreationMap[pathKey]) {
                const foundIndex = this.findIndexByConditionsCreationMap(tempValue, field.conditionsCreationMap, currentPath);
                if (foundIndex !== -1) {
                  indexValue = foundIndex;
                }
              }
            }

            // FIX 2026-02-16: Pour les tableaux imbriqués répétables (tenderer, subcontractor, maincontractor),
            // ne PAS forcer l'index à 0 si l'index demandé dépasse la longueur du tableau.
            // Cela permet aux nouveaux éléments ajoutés (ex: GR-Tenderer_1) d'avoir des champs vides
            // au lieu de récupérer les données du premier élément.
            const nestedRepeatableArrays = ['tenderer', 'subcontractor', 'maincontractor'];
            const currentArrayName = currentPath.replaceAll('[]', '').toLowerCase();
            const isNestedRepeatableArray = nestedRepeatableArrays.includes(currentArrayName);

            if (tempValue?.length === 1 && !isNestedRepeatableArray) {
              indexValue = 0;
            }

            // Si l'index demandé dépasse la longueur du tableau, retourner null
            if (tempValue && indexValue >= tempValue.length) {
              value = null;
            } else if (tempValue) {
              value = tempValue[indexValue];
            } else {
              value = null;
            }
          } else {
            // TODO : camelCase
            value = value[currentPath];
          }
          if (!value) {
            if (value === undefined) {
              return null;
            }
            break;
          }
        }


        return value;
      }

    }
  }

  public static getValidators(field: Field, formName): ValidatorFn[] {
    //prep rules
    // récupérer les rules , traiter les patterns et envoyer ça au getValidator
    let rules = '';

    if (field.maxLength) {
      rules += 'maxSize_' + field.maxLength;
    }
    if (field.pattern) {
      if (rules != '') {
        rules += '|';
      }
      rules += 'pattern_' + this.validatorService.getNoRules(field.pattern);
      //todo
      //rules += "maxSize_"+field.maxLength;
    }

    // validators.Compose , chaque condition
    if (this.doesNotHaveMandatoryWithCondition(field, formName) && this.doesNotHaveForbiddenWithCondition(field, formName) && field.mandatory) {
      if (rules != '') {
        rules += '|';
      }
      rules += field.mandatory.includes(formName?.split('E_FORM_')[1]) ? 'required' : '';
    }

    return (rules != '') ? this.validatorService.getValidator(rules) : undefined;
  }

  private static specificField(canNavigateInStepperService: CanNavigateInStepperService, returnedValue: string, field: Field) {
    let valReturn = returnedValue;
    switch (field.id) {
      case 'BT-140-notice':
        valReturn = canNavigateInStepperService.eformMotifChange.split('.').pop();
        break;

      case 'BT-701-notice':
        if (canNavigateInStepperService.eformCreatingChange) {
          canNavigateInStepperService.eformIdPreviousForm = returnedValue;
        }
        break;

      case 'BT-757-notice':
        if (canNavigateInStepperService.eformCreatingChange) {
          canNavigateInStepperService.eformVersionPreviousForm = returnedValue;
          const valNum = Number(returnedValue) + 1;
          valReturn = valNum.toString().padStart(2, '0');
        }
        break;

      case 'BT-758-notice':
        if (canNavigateInStepperService.eformCreatingChange) {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(canNavigateInStepperService.eformIdParentNotice)) {
            valReturn = canNavigateInStepperService.eformIdParentNotice + '-' + canNavigateInStepperService.eformVersionPreviousForm;
          } else {
            valReturn = canNavigateInStepperService.eformIdParentNotice.replace(/-\d+$/, '') + '-' + canNavigateInStepperService.eformVersionPreviousForm;
          }
        }
        break;

      case 'OPP-090-Procedure':
        if (canNavigateInStepperService.idParentFluxBoampEform) {
          valReturn = canNavigateInStepperService.idParentFluxBoampEform;
        }
        break;

      default:
        break;
    }
    return valReturn;
  }

  private static isNewEformGroupToCreate(prefixId, currentFormGroup, currentElement): boolean {
    if (prefixId) {
      return currentFormGroup.controls[currentElement].controls.filter(c => c.prefixEform === undefined || c.prefixEform.slice(0, c.prefixEform.lastIndexOf('_')) === prefixId.slice(0, prefixId.lastIndexOf('_'))).length === 0;
    } else {
      return false;
    }
  }

  private static pushEformGroupAtRightPlace(currentFormGroup, currentElement, prefixId, groupOrSectionId, noticeValue, pathElements, name: string, conditionsCreation): number {
    //On cherche l'index du dernier element partageant le même "nom" et on l'ajoute juste apres
    if (+prefixId.split('_')[1] > 0) {
      let previousPrefixId = prefixId.split('_')[0];
      //l'index 0 n'est pas indiqué dans le prefixId, dans ce cas on utilise uniquement la premiere partie
      if (+prefixId.split('_')[1] > 1) {
        previousPrefixId += '_' + (+prefixId.split('_')[1] - 1);
      }
      if (!previousPrefixId.includes('_')) {
        previousPrefixId += '_0';
      }
      let previousIndex = +currentFormGroup.controls[currentElement].controls.findIndex(c =>
        c.prefixEform.slice(0, c.prefixEform.lastIndexOf('_')) === previousPrefixId);
      // Si l'index du précédent existe et qu'on ne doit pas l'ajouter en dernier
      if (previousIndex !== -1 && (previousIndex + 1 !== currentFormGroup.controls[currentElement].controls.length)) {
        currentFormGroup.controls[currentElement].controls?.splice(previousIndex + 1, 0, new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
        //On modifie aussi l'objet qui contient les valeurs pour l'affichage
        if (noticeValue) {
          let tempObj;
          pathElements.forEach((path, index) => {
            if (index < (pathElements.length - 1)) {
              if (!path.includes('[]')) {
                if (tempObj) {
                  if (Array.isArray(tempObj) && tempObj.length == 0) {
                    tempObj = JSON.parse(JSON.stringify({[path]: {}}))[path];
                  } else {
                    tempObj = tempObj[path];
                  }
                } else {
                  tempObj = noticeValue[path];
                }
              } else {
                path = path.replace('[]', '');
                if (JSON.stringify(tempObj) == '{}') {
                  tempObj = JSON.parse(JSON.stringify({[path]: []}))[path];
                } else {
                  tempObj = tempObj?.[path];
                  //tempObj represente la liste dans laquelle on doit décaler les valeurs comme on l'a fait dans le form
                  tempObj = tempObj?.splice(previousIndex + 1, 0, {});
                }
              }
            }
          });
        }
        return previousIndex + 1;
      } else {
        currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
        return currentFormGroup.controls[currentElement].controls.length - 1;
      }
    } else {
      //sinon on le met à la suite dans les controls
      currentFormGroup.controls[currentElement].push(new EformGroup(prefixId, groupOrSectionId, name, conditionsCreation, {}));
      return currentFormGroup.controls[currentElement].controls.length - 1;
    }
  }


  private static getLastListName(field) {    let pathSplitted = field.jsonPath.split('.');
    pathSplitted.pop();
    return pathSplitted.join('.') + '.listName=\'' + field.listname + '\'';
  }

  /**
   * Extrait le listname depuis conditionsCreationMap.
   * Cherche un pattern comme [TendererRequirementTypeCode/&listName='XXX'] dans specifictendererrequirement
   */
  private static extractListnameFromConditionsCreationMap(conditionsCreationMap: any, field?: Field): string | null {
    if (!conditionsCreationMap) return null;

    // 🔧 FIX 2026-03-09: Pour les champs avec isJsonPathAlreadyUsed=true qui partagent le même jsonPath,
    // il faut extraire le listname depuis la CLÉ SPÉCIFIQUE mentionnée dans le jsonPath,
    // pas depuis toutes les clés de conditionsCreationMap.
    //
    // Exemple pour BT-821-Lot et BT-71-Lot:
    // - jsonPath: "...tendererqualificationrequest[].specifictendererrequirement[].tendererrequirementtypecode.val"
    // - conditionsCreationMap: {
    //     tendererqualificationrequest: ["[not(SpecificTendererRequirement)]", "[SpecificTendererRequirement]", "[not(SpecificTendererRequirement)]"],
    //     specifictendererrequirement: ["[TendererRequirementTypeCode/&listName='selection-criteria-source']"] // BT-821
    //   }
    // - On doit chercher dans 'specifictendererrequirement', pas dans 'tendererqualificationrequest'

    let targetKey = null;

    // Si on a le field, extraire la dernière clé du jsonPath qui est un tableau (contient [])
    if (field && field.jsonPath) {
      const pathParts = field.jsonPath.split('.');
      // Chercher la dernière partie qui contient []
      for (let i = pathParts.length - 1; i >= 0; i--) {
        if (pathParts[i].includes('[]')) {
          targetKey = pathParts[i].replace('[]', '');
          break;
        }
      }
    }

    // Si on a trouvé une targetKey, chercher uniquement dans cette clé
    if (targetKey && conditionsCreationMap[targetKey]) {
      const conditions = conditionsCreationMap[targetKey];
      if (conditions && Array.isArray(conditions)) {
        for (const condition of conditions) {
          const match = condition.match(/&listName='([^']+)'/);
          if (match && match[1]) {
            return match[1];
          }
        }
      }
    }

    // Sinon, comportement original : chercher dans TOUTES les clés
    for (const key of Object.keys(conditionsCreationMap)) {
      const conditions = conditionsCreationMap[key];
      if (conditions && Array.isArray(conditions)) {
        for (const condition of conditions) {
          // Pattern: [TendererRequirementTypeCode/&listName='missing-info-submission']
          // ou [ContractingSystemTypeCode/&listName='framework-agreement']
          const match = condition.match(/&listName='([^']+)'/);
          if (match && match[1]) {
            return match[1];
          }
        }
      }
    }
    return null;
  }

  /**
   * Vérifie si le conditionsCreationMap contient des conditions spéciales
   * (pas juste procurementprojectlot). Ces conditions indiquent que le champ
   * doit chercher un élément spécifique dans un tableau partagé.
   * Ex: BT-761-Lot a tendererqualificationrequest: ["[not(SpecificTendererRequirement)]"]
   */
  private static hasSpecialConditionsCreationMap(conditionsCreationMap: any): boolean {
    if (!conditionsCreationMap) return false;

    // Les clés "spéciales" qui indiquent une recherche par conditions
    const specialKeys = ['tendererqualificationrequest', 'specifictendererrequirement'];

    for (const key of specialKeys) {
      if (conditionsCreationMap[key] && Array.isArray(conditionsCreationMap[key]) && conditionsCreationMap[key].length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Cherche l'index de l'élément du tableau basé sur conditionsCreationMap.
   * Utilisé quand le listname n'est pas trouvé dans les données.
   * Ex: BT-761-Lot cherche un TQR SANS SpecificTendererRequirement ([not(SpecificTendererRequirement)])
   */
  private static findIndexByConditionsCreationMap(arrayValue: any[], conditionsCreationMap: any, currentPath: string): number {
    const pathKey = currentPath.replace('[]', '').toLowerCase();
    const conditions = conditionsCreationMap[pathKey];

    // 🔧 FIX 2026-03-09: Si les conditions ne sont pas directement sur le chemin actuel,
    // chercher si elles sont sur des sous-chemins (ex: attachment pour callfortendersdocumentreference)
    if (!conditions || !Array.isArray(conditions)) {
      // Chercher toutes les conditions qui pourraient s'appliquer aux sous-éléments
      const allConditions: {[key: string]: string[]} = {};
      for (const key in conditionsCreationMap) {
        if (conditionsCreationMap.hasOwnProperty(key) && key !== pathKey) {
          allConditions[key] = conditionsCreationMap[key];
        }
      }

      // S'il y a des conditions sur des sous-chemins, chercher l'élément qui les respecte
      if (Object.keys(allConditions).length > 0) {
        for (let i = 0; i < arrayValue.length; i++) {
          const element = arrayValue[i];
          let allMatch = true;

          for (const subKey in allConditions) {
            if (allConditions.hasOwnProperty(subKey)) {
              const subConditions = allConditions[subKey];

              // 🔧 FIX 2026-03-16: Si les conditions contiennent '../' (référence au parent),
              // évaluer sur l'élément parent (element) au lieu du sous-élément (element[subKey]).
              // Ex: condition "[../DocumentType/&text()='non-restricted-document']" sur "attachment"
              // → DocumentType est sur le parent (callfortendersdocumentreference[i]), pas sur attachment
              const hasParentReference = subConditions.some(cond => cond.includes('../'));
              const elementToCheck = hasParentReference ? element : element[subKey];

              // Vérifier si l'élément respecte les conditions
              const matches = this.matchesConditionsCreationMap(elementToCheck, subConditions);
              if (!matches) {
                allMatch = false;
                break;
              }
            }
          }

          if (allMatch) {
            return i;
          }
        }
      }

      return -1;
    }

    for (let i = 0; i < arrayValue.length; i++) {
      const element = arrayValue[i];
      const matches = this.matchesConditionsCreationMap(element, conditions);

      if (matches) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Récupère une valeur imbriquée dans un objet en utilisant un chemin séparé par des points.
   *
   * @param obj L'objet dans lequel chercher
   * @param path Le chemin vers la valeur (ex: "parametercode.val")
   * @returns La valeur trouvée ou undefined
   */
  public static getNestedValue(obj: any, path: string): any {
    if (!obj || !path) {
      return undefined;
    }

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = current[key];
    }

    return current;
  }

  /**
   * Vérifie si un élément correspond aux conditions du conditionsCreationMap.
   * Utilise le service mutualisé ConditionsCreationMapService pour l'évaluation des conditions.
   */
  private static matchesConditionsCreationMap(element: any, conditions: string[]): boolean {
    return this.conditionsCreationMapService.matchesConditions(element, conditions);
  }


  /**
   * Cherche l'index de l'élément du tableau qui contient un enfant avec le listname spécifié.
   * Cette méthode parcourt récursivement les éléments pour trouver celui qui contient
   * le bon listname dans ses enfants.
   */
  public static findIndexByListName(arrayValue: any[], keysArray: string[], currentKeyIndex: number, targetListName: string): number {
    // Construire le chemin restant pour trouver le listname
    const remainingPath = keysArray.slice(currentKeyIndex + 1);

    for (let i = 0; i < arrayValue.length; i++) {
      const element = arrayValue[i];
      if (this.hasChildWithListName(element, remainingPath, targetListName)) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Cherche l'index de l'élément du tableau en utilisant le listname enrichi sur l'objet intermédiaire.
   *
   * Pour BT-765-Lot et BT-766-Lot:
   * - arrayValue = contractingSystem[]
   * - remainingPath = ["contractingsystemtypecode"]
   * - On cherche contractingSystem[i].contractingSystemTypeCode.listname === targetListName
   */
  private static findIndexByEnrichedListName(arrayValue: any[], keysArray: string[], currentKeyIndex: number, targetListName: string): number {
    // Construire le chemin restant après le tableau
    const remainingPath = keysArray.slice(currentKeyIndex + 1);

    if (remainingPath.length === 0) {
      return -1;
    }

    for (let i = 0; i < arrayValue.length; i++) {
      const element = arrayValue[i];
      // Naviguer vers l'objet intermédiaire en suivant remainingPath
      let currentObj = element;
      for (const pathPart of remainingPath) {
        if (!currentObj) break;
        // Chercher la clé avec correspondance case-insensitive
        const actualKey = this.findKeyIgnoreCase(currentObj, pathPart.replace('[]', ''));
        if (actualKey) {
          currentObj = currentObj[actualKey];
        } else {
          currentObj = null;
          break;
        }
      }

      // Vérifier si cet objet a le bon listname
      if (currentObj && currentObj.listname === targetListName) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Trouve une clé dans un objet en ignorant la casse.
   */
  private static findKeyIgnoreCase(obj: any, keyName: string): string | null {
    if (!obj || typeof obj !== 'object') {
      return null;
    }

    const lowerKey = keyName.toLowerCase();
    for (const key of Object.keys(obj)) {
      if (key.toLowerCase() === lowerKey) {
        return key;
      }
    }
    return null;
  }

  /**
   * Vérifie récursivement si un élément contient un enfant avec le listname spécifié.
   * Cherche le listname dans TOUT l'élément, pas juste dans le chemin spécifique.
   */
  public static hasChildWithListName(element: any, remainingPath: string[], targetListName: string): boolean {
    if (!element) return false;

    // 🔧 FIX: Chercher le listname dans TOUT l'élément récursivement
    // Car le listname peut être sur un élément frère (ex: tendererrequirementtypecode)
    // et pas sur l'élément cible (ex: description)
    if (this.findListnameInObject(element, targetListName)) {
      return true;
    }

    return false;
  }

  /**
   * Cherche récursivement si un objet contient un champ avec le listname spécifié.
   */
  private static findListnameInObject(obj: any, targetListName: string): boolean {
    if (!obj || typeof obj !== 'object') return false;

    // Vérifier si l'objet lui-même a le listname
    if (obj.listname === targetListName) {
      return true;
    }

    // Parcourir toutes les propriétés
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (Array.isArray(value)) {
        for (const item of value) {
          if (this.findListnameInObject(item, targetListName)) {
            return true;
          }
        }
      } else if (typeof value === 'object' && value !== null) {
        if (this.findListnameInObject(value, targetListName)) {
          return true;
        }
      }
    }

    return false;
  }

  private static doesNotHaveMandatoryWithCondition(field: Field, formName: any): boolean {
    if (!field.mandatoryWithCondition) {
      return true;
    }

    const formNumber = formName?.split('E_FORM_')[1];
    return !field.mandatoryWithCondition.some(condition => this.conditionFormsIncludeFormNumber(condition, formNumber));
  }

  private static doesNotHaveForbiddenWithCondition(field: Field, formName: any): boolean {
    if (!field.forbiddenWithCondition) {
      return true;
    }

    const formNumber = formName?.split('E_FORM_')[1];
    return !field.forbiddenWithCondition.some(condition => this.conditionFormsIncludeFormNumber(condition, formNumber));
  }

  private static conditionFormsIncludeFormNumber(condition: any, formNumber: string | undefined): boolean {
    return condition.forms?.includes(formNumber);
  }

  private static transformIndexedPath(path) {
    const regex = /\[\](\d+)/g;
    let matches = [...path.matchAll(regex)];

    let resultParts = [];
    let currentPath = '';
    let lastIndex = 0;

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const indexValue = match[1];
      const start = match.index;
      const end = match.index + match[0].length;

      // Partie du chemin avant []n
      const segment = path.slice(lastIndex, start); // exemple: contractnotice.contractingparty

      // Ajouter proprement au chemin cumulatif
      if (currentPath && !currentPath.endsWith('.') && !segment.startsWith('.')) {
        currentPath += '.';
      }
      currentPath += segment + '[]';

      if (i < matches.length - 1) {
        // Pas le dernier => on ajoute l'_INDEX_n_SEPSEP_
        resultParts.push(currentPath + `_INDEX_${indexValue}`);
      }

      lastIndex = end;
    }

    // Ajouter le reste du chemin après le dernier index
    const finalRest = path.slice(lastIndex);
    const lastMatch = matches[matches.length - 1];
    if (lastMatch) {
      const lastIndexValue = lastMatch[1];
      if (currentPath && !currentPath.endsWith('.') && !finalRest.startsWith('.')) {
        currentPath += '.';
      }
      currentPath += finalRest;

      // Ajouter le dernier _INDEX_n à la toute fin
      resultParts.push(currentPath + `_INDEX_${lastIndexValue}`);
    }

    return resultParts.join('_SEPSEP_').replace('._','_');
  }
}
